import React from 'react';
import { Brain, Heart, Users, TrendingUp, Target, Sparkles, Clock, AlertCircle, CheckCircle, ArrowRight } from 'lucide-react';

const MindaeraOnePager = () => {
    return (
        <div className="max-w-4xl mx-auto bg-white p-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center mr-3">
                        <Brain className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <h1 className="text-3xl font-bold text-gray-900">Mindaera</h1>
                        <p className="text-sm text-gray-600">AI-powered therapeutic matching that actually works</p>
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-sm font-bold text-purple-600">Seeking $500K</p>
                    <p className="text-xs text-gray-500">First institutional round</p>
                </div>
            </div>

            {/* Section 1: Why We're The Perfect Team */}
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Users className="w-6 h-6 text-purple-600 mr-2" />
                    Why We're The Perfect Team to Build This
                </h2>
                
                <div className="space-y-4">
                    <div>
                        <p className="font-bold text-gray-800">Dr. David Stephens - Chief Psychology Officer</p>
                        <p className="text-sm text-gray-700">
                            Former Dean of University of the Rockies & Director of Mental Health for Missouri and Colorado. 
                            <span className="font-semibold"> 35 years watching the mental health system fail patients.</span> Built 
                            Peak Neuropsychology from scratch to 8-figure revenue. Knows exactly why current matching fails.
                        </p>
                    </div>
                    
                    <div>
                        <p className="font-bold text-gray-800">Erik Rüütel - CEO</p>
                        <p className="text-sm text-gray-700">
                            Built Addicere from 0 to 10,000+ active patients. Former Deputy Governor of Tallinn Prison - 
                            <span className="font-semibold"> witnessed mental health crisis at scale.</span> Executive Crisis Advisor 
                            to Fortune 500 CEOs. Second-time founder who learned from first exit.
                        </p>
                    </div>
                    
                    <div>
                        <p className="font-bold text-gray-800">Youssef El Moumni - CTO</p>
                        <p className="text-sm text-gray-700">
                            3x founder, 2x patent holder. Built Realfied's AI matching engine (acquired). 
                            <span className="font-semibold"> Obsessed with solving matching problems since 2015.</span> Former 
                            performance engineer who scaled systems to millions of users.
                        </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4 border border-purple-200">
                        <p className="text-sm font-bold text-purple-700">
                            Why us, why now: We've treated 30,000+ patients, built 3 successful practices, and watched 
                            every major platform fail at the one thing that matters - matching based on therapeutic fit. 
                            We're not tech people trying healthcare or therapists learning code. We're both.
                        </p>
                    </div>
                </div>
            </div>

            {/* Section 2: 5-Year Vision */}
            <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <Target className="w-6 h-6 text-blue-600 mr-2" />
                    Our 5-Year Vision
                </h2>
                
                <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4">
                        <p className="font-bold text-gray-800 mb-2">Year 1-2: The Foundation</p>
                        <p className="text-sm text-gray-700">
                            100,000 successful matches. Become the gold standard for therapeutic matching. 
                            Insurance partnerships locked. Amy becomes the first name people think of when seeking therapy.
                        </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4">
                        <p className="font-bold text-gray-800 mb-2">Year 3-4: The Platform</p>
                        <p className="text-sm text-gray-700">
                            1M+ matches completed. Expand beyond matching - AI-powered session insights, outcome tracking, 
                            therapist training. Every therapist wants to be on Mindaera because we send them perfect-fit clients.
                        </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4">
                        <p className="font-bold text-gray-800 mb-2">Year 5: The Revolution</p>
                        <p className="text-sm text-gray-700">
                            <span className="font-semibold">Mindaera becomes the infrastructure layer for mental health.</span> Every 
                            insurance company, healthcare system, and employer uses our matching engine. We've fundamentally changed 
                            how people find help - from desperate Google searches to confident connections.
                        </p>
                    </div>
                    
                    <p className="text-sm font-bold text-green-700 text-center mt-4">
                        Exit opportunity: $1B+ acquisition by Teladoc, Amwell, or major insurer
                    </p>
                </div>
            </div>

            {/* Section 3: Evidence We're Right */}
            <div className="bg-gradient-to-r from-green-50 to-yellow-50 rounded-xl p-6 mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
                    The Evidence We're On The Right Track
                </h2>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-white rounded-lg p-4">
                        <p className="text-2xl font-bold text-purple-600 mb-1">95%</p>
                        <p className="text-sm font-semibold text-gray-800">Match Satisfaction</p>
                        <p className="text-xs text-gray-600">
                            Beta users report feeling "instantly understood" by their matched therapist
                        </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4">
                        <p className="text-2xl font-bold text-blue-600 mb-1">3.2x</p>
                        <p className="text-sm font-semibold text-gray-800">Better Retention</p>
                        <p className="text-xs text-gray-600">
                            Our matches stay in therapy 3.2x longer than industry average
                        </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4">
                        <p className="text-2xl font-bold text-green-600 mb-1">$1.7B</p>
                        <p className="text-sm font-semibold text-gray-800">Competitor Revenue</p>
                        <p className="text-xs text-gray-600">
                            BetterHelp/Talkspace proved demand despite terrible matching
                        </p>
                    </div>
                    
                    <div className="bg-white rounded-lg p-4">
                        <p className="text-2xl font-bold text-orange-600 mb-1">73%</p>
                        <p className="text-sm font-semibold text-gray-800">Organic Referrals</p>
                        <p className="text-xs text-gray-600">
                            Beta users actively recommend us without incentives
                        </p>
                    </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 border border-green-200">
                    <p className="text-sm font-semibold text-gray-800 mb-2">Most Compelling Evidence:</p>
                    <p className="text-sm text-gray-700">
                        Three major insurance companies approached us after seeing our matching results. They're desperate 
                        for a solution that actually reduces therapy dropout rates. One offered to pilot with 50,000 members 
                        if we can scale by Q2 2025.
                    </p>
                </div>
            </div>

            {/* The Problem We Solve */}
            <div className="bg-red-50 rounded-xl p-4 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
                    The Massive Problem Nobody's Solving Right
                </h3>
                <p className="text-sm text-gray-700 mb-2">
                    Current platforms are just therapist directories with a quiz. They match on availability and insurance, 
                    not compatibility. Result: 70% quit searching, 75% who find someone quit within 3 sessions.
                </p>
                <p className="text-sm font-bold text-red-700">
                    We're the only platform using voice AI to match on what actually matters: personality fit, 
                    communication style, and therapeutic rapport.
                </p>
            </div>

            {/* Use of Funds */}
            <div className="bg-gray-100 rounded-xl p-4 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Your $500K Gets Us To:</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                        <p className="font-semibold text-gray-800">Product (30%)</p>
                        <p className="text-xs text-gray-600">Voice AI improvements, therapist dashboard</p>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-800">Operations (30%)</p>
                        <p className="text-xs text-gray-600">Core team, infrastructure, patents</p>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-800">Growth (30%)</p>
                        <p className="text-xs text-gray-600">1,000 therapists, 10K users, insurance pilot</p>
                    </div>
                    <div>
                        <p className="font-semibold text-gray-800">Compliance (10%)</p>
                        <p className="text-xs text-gray-600">HIPAA, state licensing, legal</p>
                    </div>
                </div>
                <p className="text-sm font-bold text-purple-700 mt-3 text-center">
                    This gets us to $2M ARR and Series A ready in 12 months
                </p>
            </div>

            {/* CTA */}
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-5 text-white text-center">
                <p className="text-lg font-bold mb-2">
                    We're Not Building Another Therapy App
                </p>
                <p className="text-sm mb-3">
                    We're building the matching infrastructure that will power the entire mental health industry. 
                    Be the investor who saw it first.
                </p>
                <div className="flex items-center justify-center gap-6 text-sm">
                    <span>📧 erik@mindaera.com</span>
                    <span>📱 Let's talk this week</span>
                </div>
            </div>
        </div>
    );
};

export default MindaeraOnePager;